import { html } from 'common-tags';

import { fields } from './shared';

/**
 * @typedef {import('../types').Brand} Brand
 * @typedef {import('../types').Template} Template
 */

/**
 * Classic bizcard template.
 * @param {Brand} brand - The brand object with theme colors and assets.
 * @returns {Template} - An object containing the email's HTML body and style block.
 */
export default (brand) => ({
  style: html`
    <style>
      :root {
        --background-desktop_only-background-color: #e0e0e0;
        --background-desktop_only-background-image: ;

        --logo-height: 3rem;
        --logo-background-image: url('${brand.logoUrl}');

        --card-background-color: ${brand.colorBackground};
        --card-color: ${brand.colorText};
        --card-font-family: '${brand.fontBase}';
        --card-font-weight: ${brand.fontWeightBase};
        --card-padding: 1.35rem;
        --card-desktop_only-border-radius: 0.75rem;
        --card-desktop_only-box-shadow: 0 0.5rem 1rem rgba(0, 0, 0, 0.25);
        --card-header-background-color: ${brand.colorPrimary};
        --card-header-color: ${brand.colorTertiary};
        --card-header-border-width: 2rem;
        --card-header-border-color: ${brand.colorTertiary};

        --profile_image-border-radius: 100%;
        --profile_image-border-color: ${brand.colorBackground};
        --profile_image-border-width: 4px;
        --profile_image-width: 8rem;
        --profile_image-background-image: ;

        --titles-color: ${brand.colorText};
        --titles-font-family: '${brand.fontHeadings}';
        --titles-font-size: 2rem;
        --titles-font-weight: ${brand.fontWeightHeadings};
        --titles-text-align: center;
        --titles-text-transform: none;

        --fields-icons-display: inline-block;
        --fields-icons-color: ${brand.colorBackground};
        --fields-icons-background-color: ${brand.colorPrimary};
        --fields-icons-padding: 0.5rem;
        --fields-labels-display: none;
        --fields-labels-color: ${brand.colorText};
        --fields-font-size: 14px;
        --fields-line-height: 2.5;

        --button-background-color: ${brand.colorPrimary};
        --button-color: ${brand.colorBackground};
        --button-border-color: ${brand.colorPrimary};
        --button-border-width: 3px;
        --button-border-radius: 0.5rem;
        --button-text-transform: none;
      }

      body {
        background-color: var(--background-desktop_only-background-color);
        background-image: var(--background-desktop_only-background-image);
        background-size: cover;
        font-family: var(--card-font-family);
        font-weight: var(--card-font-weight);
      }

      h1,
      h2,
      h3,
      h4,
      h5,
      h6 {
        color: var(--titles-color);
        font-family: var(--titles-font-family);
        font-weight: var(--titles-font-weight);
        text-transform: var(--titles-text-transform);
      }

      h1 {
        font-size: var(--titles-font-size);
      }

      h3 {
        font-size: calc(var(--titles-font-size) * 0.65);
        text-transform: uppercase;
      }

      nav {
        background-color: transparent;
        padding: 0;
      }

      #logo {
        background-image: var(--logo-background-image);
        background-position: center;
        background-repeat: no-repeat;
        background-size: contain;
        height: var(--logo-height);
        width: 100%;
      }

      .card {
        background-color: var(--card-background-color);
        color: var(--card-color);
        margin: 0 auto;
        min-height: 100vh;
        overflow: hidden;
        width: 100%;
      }

      .card a {
        color: var(--card-color);
      }

      @media (min-width: 576px) {
        nav {
          box-shadow: var(--nav-box-shadow);
          margin-bottom: 1rem;
        }
        .card {
          border-radius: var(--card-desktop_only-border-radius);
          box-shadow: var(--card-desktop_only-box-shadow);
          min-height: 0;
          width: 3.5in;
        }
      }

      .card header {
        align-items: center;
        aspect-ratio: 4/3;
        background-color: var(--card-header-background-color);
        border-bottom: var(--card-header-border-width) solid var(--card-header-border-color);
        display: flex;
        flex-direction: column;
        justify-content: center;
        margin-bottom: calc(var(--profile_image-width) / 2);
        position: relative;
      }

      .card header a {
        display: block;
        width: 100%;
      }

      .card .title {
        background-color: var(--card-background-color);
        color: var(--card-color);
        padding: 0 var(--card-padding);
        position: relative;
        text-align: var(--titles-text-align);
      }

      .card .content {
        padding: var(--card-padding);
      }

      .card footer {
        padding-top: var(--card-padding);
      }

      .card button {
        background-color: var(--button-background-color);
        border: var(--button-border-width) solid var(--button-border-color);
        border-radius: var(--button-border-radius);
        color: var(--button-color);
        font-size: 1.25rem;
        padding: 0.5rem;
        text-transform: var(--button-text-transform);
        width: 100%;
      }

      .card connect-button button,
      .card install-button button {
        background-color: transparent;
        color: var(--button-border-color);
        font-size: 1rem;
        margin-top: 0.5rem;
      }

      .profile-image {
        aspect-ratio: 1/1;
        background-color: var(--card-background-color);
        border: var(--profile_image-border-width) solid var(--profile_image-border-color);
        border-radius: var(--profile_image-border-radius);
        bottom: 0;
        left: 50%;
        overflow: hidden;
        position: absolute;
        transform: translate(-50%, 50%) translateY(calc(var(--card-header-border-width) / 2));
        width: var(--profile_image-width);
        z-index: 1000;
      }

      li {
        align-items: center;
        display: grid;
        font-size: var(--fields-font-size);
        grid-template-columns: auto 1fr;
        grid-template-rows: auto auto;
        line-height: var(--fields-line-height);
      }

      li i {
        background-color: var(--fields-icons-background-color);
        border-radius: 12rem;
        color: var(--fields-icons-color);
        display: var(--fields-icons-display);
        font-size: var(--fields-font-size);
        grid-column: 1 / 2;
        grid-row: 1 / 2;
        margin-right: 0.35rem;
        padding: var(--fields-icons-padding);
        text-align: center;
      }

      li a,
      li span {
        align-self: end;
        grid-column: 2 / 3;
        grid-row: 1 / 2;
      }

      li small {
        align-self: start;
        color: var(--fields-labels-color);
        display: var(--fields-labels-display);
        font-size: 0.8em;
        grid-column: 2 / 3;
        grid-row: 2 / 3;
      }
    </style>
  `,
  body: html`
    <nav data-element="header"></nav>

    <div class="card">
      <header data-element="card-header">
        <a href="${brand.companyUrl}" target="_blank">
          <div id="logo" data-element="logo"></div>
        </a>
        <div class="profile-image" data-element="profile-image">
          <profile-image src="{{ image_url }}"></profile-image>
        </div>
      </header>

      <div class="title" data-element="titles">
        <h1>{{ name }}</h1>
        <h3>{{ title }}</h3>
      </div>

      <div class="content">
        ${fields}

        <footer data-element="button">
          <user-only>
            <share-button>
              <button>Share</button>
            </share-button>
            <install-button>
              <button>Add to Home Screen</button>
            </install-button>
          </user-only>

          <shared-only>
            <vcard-button>
              <button>Add Contact</button>
            </vcard-button>
          </shared-only>
        </footer>
      </div>
    </div>
  `
});
